import { create } from "zustand";
import type { SearchSessionContext } from "./image-search-session";

/**
 * Mini-App image-search modal state machine — a port of
 * apps/web/lib/image-search-flow.ts.
 *
 * Decouples the trigger (camera button in the hero / catalog header) from the
 * modal rendering (a single <ImageSearchModals/> mounted at the root). Phases:
 *
 *  - idle   : no modal
 *  - upload : upload sheet (pick file / drag / preview / reselect)
 *  - crop   : region-select overlay (a displayable image src is ready)
 *
 * `src` is a displayable address for <img> (File → data-URL after downscale).
 * Mini only ever stashes stable data-URLs (no blob: objectURLs survive a route
 * change), so there is no objectURL to revoke here.
 */
export type ImageSearchPhase = "idle" | "upload" | "crop";
export type ImageSearchTargetChannel = "1688" | "taobao" | "weidian";

const DEFAULT_TARGET_CHANNEL: ImageSearchTargetChannel = "1688";
const TARGET_CHANNELS = new Set<string>(["1688", "taobao", "weidian"]);

function normalizeTargetChannel(channel: string | undefined): ImageSearchTargetChannel {
  return channel && TARGET_CHANNELS.has(channel)
    ? (channel as ImageSearchTargetChannel)
    : DEFAULT_TARGET_CHANNEL;
}

interface ImageSearchFlowState {
  phase: ImageSearchPhase;
  src: string | null;
  cropContext: SearchSessionContext | null;
  targetChannel: ImageSearchTargetChannel;
  openUpload: (channel?: string) => void;
  openCrop: (src: string, context?: SearchSessionContext, channel?: string) => void;
  close: () => void;
}

export const useImageSearchFlow = create<ImageSearchFlowState>((set, get) => ({
  phase: "idle",
  src: null,
  cropContext: null,
  targetChannel: DEFAULT_TARGET_CHANNEL,
  openUpload: (channel) => {
    set({
      phase: "upload",
      src: null,
      cropContext: null,
      targetChannel: normalizeTargetChannel(channel),
    });
  },
  openCrop: (src: string, context?: SearchSessionContext, channel?: string) => {
    set({
      phase: "crop",
      src,
      cropContext: context ?? null,
      targetChannel: normalizeTargetChannel(channel ?? get().targetChannel),
    });
  },
  close: () => {
    set({ phase: "idle", src: null, cropContext: null, targetChannel: DEFAULT_TARGET_CHANNEL });
  },
}));
